import React, { useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { makeStyles, createStyles } from '@material-ui/core';
import {
  Grid, Button, Card, CardContent, TextField
} from '@mui/material';
import {
  Formik, Form, FormikProps
} from 'formik';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useSnackbar } from 'notistack';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  differenceInCalendarDays, endOfDay, startOfDay, subDays
} from 'date-fns';
import _ from 'lodash';

import { useAuth } from '../../../contexts/auth';
import WrapperSimple from '../../../layout-components/ExampleWrapperSimple';
import Dictionary from '../../../models/interfaces/dictionary';
import callService, { CallAdjustmentListItem } from '../../../services/callService';
import { AdjustmentForm } from './form';
import subsidiaryService from '../../../services/subsidiaryService';
import { StatusCallList } from '../../../models/enums/statusCall';
import { toBrazilDate, toBrazilTime } from '../../../components/mask';
import Loading from '../../../components/loading';

interface Formulario {
  subsidiary: string | null;
  status: Dictionary | null;
  clientName: string;
  startDate: Date | null;
  endDate: Date | null;
}

const useStyles = makeStyles(() => createStyles({
  selectRow: {
    cursor: 'pointer'
  },
  blockedRow: {
    cursor: 'not-allowed'
  }
}));

const page = () => {
  const classes = useStyles();
  const current = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = React.useState<CallAdjustmentListItem[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [subsidiaries, setSubsidiaries] = React.useState<Dictionary[]>([]);
  const [selectedCallId, setSelectedCallId] = React.useState<string | null>(null);
  const { subsidiaryId, isReadOnly } = current;

  useEffect(() => {
    if (subsidiaryId !== undefined) return;
    subsidiaryService.getByPaginationAsync(1, 200).then((result) => {
      setSubsidiaries(result.data.map((item: any) => ({
        key: item.id,
        value: item.description
      })));
    });
  }, [subsidiaryId]);

  const handleDestinationUpdated = (index: number, address: string) => {
    if (!selectedCallId) return;
    setRows((currentRows) => currentRows.map((row) => {
      if (row.id !== selectedCallId) return row;
      return {
        ...row,
        destinations: row.destinations.map((item, itemIndex) => (itemIndex === index ? address : item))
      };
    }));
  };

  if (isReadOnly) return <Redirect to="/deliveries" />;

  return (
    <>
      <WrapperSimple sectionHeading="Ajustes/Ocorrências">
        <Formik
          initialValues={{
            subsidiary: null,
            status: null,
            clientName: '',
            startDate: startOfDay(subDays(new Date(), 2)),
            endDate: endOfDay(new Date())
          }}
          onSubmit={async (values: Formulario, actions) => {
            setIsLoading(true);

            let subsidiaryIdSelected = subsidiaryId;
            if (!subsidiaryIdSelected) subsidiaryIdSelected = values.subsidiary;

            if (!subsidiaryIdSelected) {
              enqueueSnackbar('Informe a franquia', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            if (!values.startDate || !values.endDate) {
              enqueueSnackbar('Informe o período', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            if (values.endDate < values.startDate || differenceInCalendarDays(values.endDate, values.startDate) > 15) {
              enqueueSnackbar('O período máximo da consulta é de 15 dias', { variant: 'warning' });
              actions.setSubmitting(false);
              setIsLoading(false);
              return;
            }

            const subsidiaryName = subsidiaries.find((item) => item.key === subsidiaryIdSelected)?.value
              || current.subsidiaries?.find((item) => item.key === subsidiaryIdSelected)?.value
              || null;

            try {
              const timezone = await subsidiaryService.getTimezone(subsidiaryIdSelected);
              const report = await callService.getCallsForAdjustment({
                subsidiaryId: subsidiaryIdSelected,
                subsidiaryName,
                startDate: values.startDate,
                endDate: values.endDate,
                timezone,
                status: values.status?.key ?? null,
                clientName: values.clientName
              });
              setRows(report);
              setSearched(true);
            } catch (error: any) {
              enqueueSnackbar(error.message ?? 'Erro ao buscar chamadas', { variant: 'error' });
              setRows([]);
            } finally {
              actions.setSubmitting(false);
              setIsLoading(false);
            }
          }}
        >
          {(props: FormikProps<Formulario>) => {
            const {
              values, handleChange, handleBlur, isSubmitting, setFieldValue
            } = props;

            return (
              <Form noValidate autoComplete="off">
                <Grid container spacing={3}>
                  {subsidiaryId === undefined && (
                    <Grid item xs={12} sm={12} md={6}>
                      <Autocomplete
                        options={subsidiaries}
                        getOptionLabel={(option: Dictionary) => option.value}
                        onChange={(event, value) => setFieldValue('subsidiary', value ? value.key : null)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            required
                            variant="outlined"
                            label="Franquia"
                            onBlur={handleBlur}
                          />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={subsidiaryId === undefined ? 6 : 4}>
                    <Autocomplete
                      options={StatusCallList}
                      getOptionLabel={(option: Dictionary) => option.value}
                      value={values.status}
                      onChange={(event, value) => setFieldValue('status', value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant="outlined"
                          label="Status"
                          placeholder="Todos"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Cliente"
                      name="clientName"
                      value={values.clientName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <KeyboardDatePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Início"
                      format="dd/MM/yyyy"
                      InputLabelProps={{ shrink: true }}
                      name="startDate"
                      onChange={(date: Date | null) => { setFieldValue('startDate', date); }}
                      value={values.startDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <KeyboardDatePicker
                      fullWidth
                      required
                      inputVariant="outlined"
                      label="Fim"
                      format="dd/MM/yyyy"
                      InputLabelProps={{ shrink: true }}
                      name="endDate"
                      onChange={(date: Date | null) => { setFieldValue('endDate', date); }}
                      value={values.endDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={4}>
                    <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} fullWidth>
                      <span className="btn-wrapper--icon">
                        <FontAwesomeIcon icon={['far', 'keyboard']} />
                      </span>
                      <span className="btn-wrapper--label">Buscar</span>
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </WrapperSimple>

      {isLoading ? <Loading /> : (
        searched && (
          <Card className="card-box mb-4">
            <CardContent className="p-0">
              <div className="table-responsive">
                <table className="table table-hover text-nowrap mb-0">
                  <thead>
                    <tr>
                      <th className="bg-white text-center">Data</th>
                      <th className="bg-white text-left">Cliente</th>
                      <th className="bg-white text-left">Destinos</th>
                      <th className="bg-white">Condutor</th>
                      <th className="bg-white text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr><td colSpan={5} className="text-center">Nenhuma informação encontrada</td></tr>
                    )}
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className={row.canAdjust ? classes.selectRow : classes.blockedRow}
                        onClick={() => {
                          if (!row.canAdjust) {
                            enqueueSnackbar('Só é possível ajustar entregas faturadas com um único destino', { variant: 'warning' });
                            return;
                          }
                          setSelectedCallId(row.id);
                        }}
                      >
                        <td className="text-center">
                          <div>{toBrazilDate(row.createdAt)}</div>
                          <span>{toBrazilTime(row.createdAt)}</span>
                        </td>
                        <td>{row.clientName}</td>
                        <td>
                          {row.destinations.length === 0 && <span className="text-black-50">Não informado</span>}
                          {row.destinations.map((address, destinationIndex) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <span key={`${row.id}-${destinationIndex}`} className="text-black-50 d-block">
                              {_.truncate(address, { length: 80, separator: '...' })}
                            </span>
                          ))}
                        </td>
                        <td>{row.driverName}</td>
                        <td className="text-center">
                          <div className={`h-auto py-0 px-3 badge badge-${row.statusHtml.color}`}>
                            {row.statusHtml.description}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )
      )}

      {selectedCallId && (
        <AdjustmentForm
          open
          callId={selectedCallId}
          onClose={() => setSelectedCallId(null)}
          onUpdated={handleDestinationUpdated}
        />
      )}
    </>
  );
};

export default page;
